import { classifyHref } from "@zplanem/web-paths"
import type { ReactNode } from "react"
import { Link } from "react-router-dom"

import { destinationToWebPath } from "./destination-to-web-path"

const LINK_CLASS = "underline underline-offset-2 break-all"

/**
 * Renders one chat-message link. Own-origin (incl. legacy-relative and
 * legacy-origin) links navigate in-app via react-router `<Link>` — we lean on
 * `<Link>` for modifier/middle-click handling so the browser opens its own tab
 * (AC12). The `to` is derived from the typed destination (NOT the raw stripped
 * string), so virtual paths like projectJournal map to a real route. External
 * links open in a new tab with safe semantics.
 */
export function ChatLink({
	href,
	ownedOrigins,
	children,
	...rest
}: {
	href: string
	ownedOrigins: readonly string[]
	children: ReactNode
}) {
	const dest = classifyHref(href, ownedOrigins)
	if (dest.kind === "external") {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className={LINK_CLASS}
				{...rest}
			>
				{children}
			</a>
		)
	}
	const searchHash = extractSearchHash(href)
	return (
		<Link
			to={destinationToWebPath(dest, searchHash)}
			className={LINK_CLASS}
			{...rest}
		>
			{children}
		</Link>
	)
}

function extractSearchHash(href: string): string {
	const m = href.match(/[?#].*$/)
	return m ? m[0] : ""
}
